import React, { useState, useEffect, useContext, useRef } from 'react';
import { Wrench, Camera, AlertTriangle, Check, Search, X, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { JobSelector } from '../common/JobSelector';
import { CustomSelect } from '../common/CustomSelect';
import { Job } from '../../types/backend.types';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import { uploadQueueService } from '../../services/upload-queue.service';
import { Camera as CameraComponent } from '../Camera';
import { isIOS } from '../../utils/platform.utils';

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
  status: string;
  // Tracker GPS (from /api/equipment) — the issue's job comes from where the
  // MACHINE sits, not where the reporter is standing.
  telematics_last_lat?: number | null;
  telematics_last_lng?: number | null;
}

interface Employee {
  employee_id: number;
  first_name: string;
  last_name: string;
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Language support
const translations = {
  en: {
    title: 'Report Equipment Issue',
    selectEquipment: 'Select Equipment',
    severity: 'Severity Level',
    low: 'Low - Minor issue, can wait',
    medium: 'Medium - Needs attention soon',
    high: 'High - Urgent, cannot operate',
    description: 'Issue Description',
    descriptionPlaceholder: 'Describe the problem in detail...',
    photo: 'Photo (Optional)',
    takePhoto: 'Take Photo',
    removePhoto: 'Remove Photo',
    selectJob: 'Job Location (Optional)',
    currentJob: 'Current Job',
    submit: 'Report Issue',
    submitting: 'Submitting...',
    success: 'Issue Reported!',
    successMessage: 'Equipment issue has been reported successfully.',
    reportAnother: 'Report Another Issue',
    required: 'Required',
    selectEquipmentFirst: 'Please select equipment',
    selectSeverity: 'Please select severity level',
    enterDescription: 'Please describe the issue'
  },
  es: {
    title: 'Reportar Problema de Equipo',
    selectEquipment: 'Seleccionar Equipo',
    severity: 'Nivel de Gravedad',
    low: 'Bajo - Problema menor, puede esperar',
    medium: 'Medio - Necesita atención pronto',
    high: 'Alto - Urgente, no puede operar',
    description: 'Descripción del Problema',
    descriptionPlaceholder: 'Describa el problema en detalle...',
    photo: 'Foto (Opcional)',
    takePhoto: 'Tomar Foto',
    removePhoto: 'Quitar Foto',
    selectJob: 'Ubicación del Trabajo (Opcional)',
    currentJob: 'Trabajo Actual',
    submit: 'Reportar Problema',
    submitting: 'Enviando...',
    success: '¡Problema Reportado!',
    successMessage: 'El problema del equipo ha sido reportado exitosamente.',
    reportAnother: 'Reportar Otro Problema',
    required: 'Requerido',
    selectEquipmentFirst: 'Por favor seleccione equipo',
    selectSeverity: 'Por favor seleccione nivel de gravedad',
    enterDescription: 'Por favor describa el problema'
  }
};

export const EquipmentIssues: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { ensureFreshLocation } = useLocation();

  // Navigate with location refresh (matches dashboard pattern)
  const navigateToTimeClock = async () => {
    await ensureFreshLocation();
    navigate('/time-clock');
  };

  const [user, setUser] = useState<Employee | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  // Form state
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredEquipment, setFilteredEquipment] = useState<Equipment[]>([]);
  const [severity, setSeverity] = useState<string>('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [autoSelectedJob, setAutoSelectedJob] = useState(false);
  // What auto-picked the job: the machine's tracker vs the reporter's clock-in.
  const [autoJobSource, setAutoJobSource] = useState<'machine' | 'clockin' | null>(null);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraCooldown, setCameraCooldown] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    user: true,
    jobs: true,
    equipment: true,
    currentJob: true,
    initializing: true
  });

  const t = translations[language];

  // Geofence helper functions
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const isInsideCircle = (userLat: number, userLng: number, centerLat: number, centerLng: number, radius: number): boolean => {
    const distance = calculateDistance(userLat, userLng, centerLat, centerLng);
    return distance <= radius;
  };

  const isInsidePolygon = (userLat: number, userLng: number, points: Array<{ lat: number; lng: number }>): boolean => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].lat, yi = points[i].lng;
      const xj = points[j].lat, yj = points[j].lng;

      const intersect = ((yi > userLng) !== (yj > userLng))
          && (userLat < (xj - xi) * (userLng - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const isUserWithinJobGeofence = (job: Job, userLat: number, userLng: number): boolean => {
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      return isInsideCircle(userLat, userLng, job.lat, job.lng, job.geofence_data.radius);
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      return isInsidePolygon(userLat, userLng, job.geofence_data.points);
    }
    // Default circular geofence of 100 meters
    const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
    return distance <= 100;
  };

  // Check if user is clocked into a job
  // Note: jobsList parameter is used to avoid stale closure issue
  const checkClockedJob = async (jobsList: Job[]): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/time/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      const data = await response.json();
      console.log('📍 Clock status check:', data);

      if (data.success && data.data?.job_id) {
        // A machine-GPS pick (deep link) may have landed while this request was
        // in flight — the machine's location wins over the reporter's clock-in.
        if (machinePickedRef.current) return true;
        const clockedJob = jobsList.find((job: Job) => job.job_id === data.data.job_id);
        if (clockedJob) {
          console.log('📍 Auto-selected job based on clock-in:', clockedJob.job_name);
          setSelectedJob(clockedJob.job_id);
          setAutoSelectedJob(true);
          setAutoJobSource('clockin');
          return true;
        } else {
          console.log('📍 Clocked into job_id', data.data.job_id, 'but not found in jobs list');
        }
      } else {
        console.log('📍 Not clocked in');
      }
      return false;
    } catch (err) {
      console.error('Error checking clocked job:', err);
      return false;
    }
  };

  // Check clocked job - accepts jobs list to avoid stale closure
  const checkClockedJobFirst = async (jobsList: Job[]) => {
    try {
      setLoadingStates(prev => ({ ...prev, currentJob: true }));
      await checkClockedJob(jobsList);

      // For admins not clocked in: will show JobSelector
      // For workers/foremen not clocked in: will show "Please clock in" message
      setLoadingStates(prev => ({ ...prev, currentJob: false, initializing: false }));
    } catch (err) {
      console.error('Error checking clocked job:', err);
      setLoadingStates(prev => ({ ...prev, currentJob: false, initializing: false }));
    }
  };

  // Styles
  const styles = {
    container: {
      padding: '20px',
      maxWidth: '600px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      padding: '16px',
      marginBottom: '16px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#ea580c',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '12px',
      padding: '4px 0'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    iconCircle: {
      width: '48px',
      height: '48px',
      backgroundColor: '#ea580c',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    headerTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '4px'
    },
    headerSubtitle: {
      fontSize: '14px',
      color: '#64748b'
    },
    content: {
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    formGroup: {
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      fontSize: '16px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    required: {
      color: '#ef4444',
      marginLeft: '4px'
    },
    searchContainer: {
      position: 'relative' as const,
      marginTop: '8px'
    },
    searchInput: {
      width: '100%',
      padding: '14px 14px 14px 44px',
      fontSize: '20px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      boxSizing: 'border-box' as const,
      backgroundColor: 'white',
      fontWeight: '500' as const
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '12px',
      top: '12px',
      color: '#9ca3af',
      pointerEvents: 'none' as const
    },
    dropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '4px',
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      maxHeight: '300px',
      overflowY: 'auto' as const,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 1000
    },
    dropdownItem: {
      padding: '14px',
      cursor: 'pointer',
      borderBottom: '1px solid #f1f5f9',
      transition: 'background-color 0.2s'
    },
    dropdownItemName: {
      fontWeight: '600' as const,
      color: '#1e293b',
      marginBottom: '6px',
      fontSize: '18px'
    },
    dropdownItemDetails: {
      fontSize: '15px',
      color: '#64748b'
    },
    selectedEquipmentCard: {
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#f8fafc',
      border: '2px solid #e5e7eb',
      borderRadius: '8px'
    },
    selectedEquipmentName: {
      fontWeight: '600' as const,
      color: '#1e293b',
      fontSize: '16px',
      marginBottom: '4px'
    },
    selectedEquipmentDetails: {
      fontSize: '14px',
      color: '#64748b'
    },
    select: {
      width: '100%',
      padding: '12px',
      fontSize: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: 'white',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      fontSize: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      minHeight: '120px',
      fontFamily: 'inherit',
      resize: 'vertical' as const
    },
    severityGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '12px'
    },
    severityButton: (isSelected: boolean, color: string) => ({
      padding: '16px',
      borderRadius: '8px',
      border: isSelected ? `3px solid ${color}` : '2px solid #e5e7eb',
      backgroundColor: isSelected ? `${color}15` : 'white',
      cursor: 'pointer',
      textAlign: 'left' as const,
      transition: 'all 0.2s',
      fontSize: '16px',
      fontWeight: isSelected ? '600' : '400'
    }),
    photoSection: {
      marginBottom: '24px'
    },
    photoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '12px',
      marginBottom: '12px'
    },
    photoPreviewContainer: {
      position: 'relative' as const,
      paddingTop: '100%',
      borderRadius: '8px',
      overflow: 'hidden',
      border: '2px solid #e5e7eb'
    },
    photoPreview: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const
    },
    removePhotoButton: {
      position: 'absolute' as const,
      top: '6px',
      right: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '28px',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    cameraButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      justifyContent: 'center'
    },
    removeButton: {
      backgroundColor: '#ef4444',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px'
    },
    submitButton: {
      width: '100%',
      backgroundColor: '#ea580c',
      color: 'white',
      padding: '16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '32px'
    },
    submitButtonDisabled: {
      width: '100%',
      backgroundColor: '#9ca3af',
      color: 'white',
      padding: '16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '32px'
    },
    errorMessage: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fca5a5',
      color: '#dc2626',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px'
    },
    successOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    successCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      textAlign: 'center' as const,
      maxWidth: '400px',
      width: '100%'
    }
  };

  // Load user and equipment
  useEffect(() => {
    const userData = localStorage.getItem('bfa_user');
    if (userData) {
      setUser(JSON.parse(userData));
      setLoadingStates(prev => ({ ...prev, user: false }));
    }

    const langData = localStorage.getItem('language');
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    // Listen for language changes
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };
    window.addEventListener('languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    if (user) {
      const initializeData = async () => {
        // Load equipment and jobs in parallel
        const [, loadedJobs] = await Promise.all([
          loadEquipment(),
          loadJobs()
        ]);
        // After jobs are loaded, check clocked job - pass jobs directly to avoid closure issue
        console.log('📍 Loaded', loadedJobs.length, 'jobs, checking clock status...');
        checkClockedJobFirst(loadedJobs);
      };

      initializeData();
    }

  }, [user]);

  // Filter equipment based on search text
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredEquipment(equipment);
    } else {
      const search = searchText.toLowerCase();
      const filtered = equipment.filter(eq =>
        eq.equipment_name.toLowerCase().includes(search) ||
        eq.bradanna_equipment_number.toLowerCase().includes(search) ||
        eq.equipment_type.toLowerCase().includes(search)
      );
      setFilteredEquipment(filtered);
    }
  }, [searchText, equipment]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-search-container]')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const loadEquipment = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEquipment(data.data);
        setLoadingStates(prev => ({ ...prev, equipment: false }));
      }
    } catch (error) {
      console.error('Error loading equipment:', error);
      setLoadingStates(prev => ({ ...prev, equipment: false }));
    }
  };

  const loadJobs = async (): Promise<Job[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/jobs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.data);
        setLoadingStates(prev => ({ ...prev, jobs: false }));
        return data.data || [];
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
      setLoadingStates(prev => ({ ...prev, jobs: false }));
    }
    return [];
  };


  const handleSelectEquipment = (eq: Equipment) => {
    setSelectedEquipment(eq);
    setSearchText(`${eq.equipment_name} (#${eq.bradanna_equipment_number})`);
    setShowDropdown(false);
  };

  // Deep-link preselect (?equipment=<id>) — e.g. Machine Hub's "Report Issue"
  // button lands here with the machine already chosen.
  const [searchParams] = useSearchParams();
  const didPreselectRef = useRef(false);
  useEffect(() => {
    if (didPreselectRef.current || selectedEquipment || equipment.length === 0) return;
    const id = parseInt(searchParams.get('equipment') || '');
    if (!id) return;
    const eq = equipment.find(e => e.equipment_id === id);
    if (eq) { handleSelectEquipment(eq); didPreselectRef.current = true; }
  }, [equipment, searchParams, selectedEquipment]);

  // Job from the MACHINE's GPS, not the reporter's. When the selected machine
  // has tracker coordinates inside a job geofence, that job wins over the
  // clock-in auto-pick (a mechanic reporting from the shop shouldn't tag the
  // issue to the shop when the machine is out on a job). No GPS or off-site →
  // keep the clock-in/manual selection.
  const machinePickedRef = useRef(false);
  useEffect(() => {
    if (!selectedEquipment || jobs.length === 0) return;
    const mLat = selectedEquipment.telematics_last_lat;
    const mLng = selectedEquipment.telematics_last_lng;
    if (mLat == null || mLng == null) return;
    const hit = jobs.find(j => j.lat != null && j.lng != null
      && isUserWithinJobGeofence(j, Number(mLat), Number(mLng)));
    if (hit) {
      machinePickedRef.current = true;
      setSelectedJob(hit.job_id);
      setAutoSelectedJob(true);
      setAutoJobSource('machine');
      console.log('📍 Job set from machine GPS:', hit.job_name);
    }
  }, [selectedEquipment, jobs]);

  const removePhoto = (index: number) => {
    const newPhotos = [...photos];
    const newPreviewUrls = [...photoPreviewUrls];

    URL.revokeObjectURL(newPreviewUrls[index]);

    newPhotos.splice(index, 1);
    newPreviewUrls.splice(index, 1);

    setPhotos(newPhotos);
    setPhotoPreviewUrls(newPreviewUrls);
  };

  const handleTakePhoto = async () => {
    if (cameraCooldown) return;

    if (isIOS()) {
      // iOS: Use native camera
      try {
        setCameraCooldown(true);

        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            // Generate preview URL
            const previewUrl = await fileToDataUrl(file);

            // Add to existing photos array
            setPhotos([...photos, file]);
            setPhotoPreviewUrls([...photoPreviewUrls, previewUrl]);
          }
        });

        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setError('Failed to capture photo. Please try again.');
        setCameraCooldown(false);
      }
    } else {
      // Android: Use custom camera component
      setShowCamera(true);
    }
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    setShowCamera(false);

    try {
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Generate preview URL
      const previewUrl = await fileToDataUrl(file);

      // Add to existing photos array
      setPhotos([...photos, file]);
      setPhotoPreviewUrls([...photoPreviewUrls, previewUrl]);

      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 1000);
    } catch (err) {
      console.error('Error processing camera capture:', err);
      setError('Failed to capture photo. Please try again.');
      setCameraCooldown(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setCameraCooldown(true);
    setTimeout(() => setCameraCooldown(false), 500);
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedEquipment) {
      setError(t.selectEquipmentFirst);
      return;
    }
    if (!severity) {
      setError(t.selectSeverity);
      return;
    }
    if (!description.trim()) {
      setError(t.enterDescription);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Get user ID from storage
      const userData = localStorage.getItem('bfa_user');
      const employeeId = userData ? JSON.parse(userData).employee_id : 0;

      // Prepare files for queue
      const queueFiles: { file: Blob; filename: string; fieldName: string }[] = photos.map((photo, index) => ({
        file: photo,
        filename: photo.name,
        fieldName: 'photos'
      }));

      // Add to queue with background upload
      await uploadQueueService.addToQueue(
        'equipment_issue',
        {
          equipment_id: selectedEquipment.equipment_id,
          severity: severity,
          issue_description: description,
          job_id: selectedJob || undefined
        },
        queueFiles,
        employeeId,
        selectedJob || undefined
      );

      setShowSuccess(true);

      // Reset form after success
      setTimeout(() => {
        resetForm();
      }, 2000);

    } catch (error) {
      console.error('Error submitting issue:', error);
      setError('Failed to submit issue. Please try again.');
    }

    setIsSubmitting(false);
  };

  const resetForm = () => {
    setSelectedEquipment(null);
    setSearchText('');
    setSeverity('');
    setDescription('');
    // Clean up photo URLs to prevent memory leaks
    photoPreviewUrls.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    setPhotos([]);
    setPhotoPreviewUrls([]);
    // Keep selectedJob - user is still clocked in to the same job
    setShowSuccess(false);
    setError(null);
  };

  const canSubmit = selectedEquipment && severity && description.trim();

  // Show loading overlay while initializing
  if (loadingStates.initializing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          Loading...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not clocked in check - different UI for admin/mechanic vs workers/foremen
  if (!selectedJob) {
    const isAdminOrMechanic = auth?.user?.role === 'admin' || auth?.user?.role === 'mechanic';

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.iconCircle}>
              <Wrench size={24} color="white" />
            </div>
            <div>
              <h1 style={styles.headerTitle}>{t.title}</h1>
              <p style={styles.headerSubtitle}>{language === 'es' ? 'Reportar un problema' : 'Report an issue'}</p>
            </div>
          </div>
        </div>
        <div style={{
          ...styles.content,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center'
        }}>
          {isAdminOrMechanic ? (
            // Admin: Show JobSelector
            <div style={{ width: '100%', maxWidth: '500px' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', color: '#64748b' }}>
                {language === 'en'
                  ? 'Please select a job to report equipment issues'
                  : 'Por favor seleccione un trabajo para reportar problemas de equipo'}
              </p>
              <JobSelector
                jobs={jobs}
                currentJob={null}
                onJobSelect={(job) => {
                  setSelectedJob(job.job_id);
                  setAutoSelectedJob(false);
                }}
                mode="admin"
                language={language}
              />
            </div>
          ) : (
            // Workers/Foremen: Must clock in first
            <div>
              <AlertTriangle size={48} style={{ color: '#f59e0b', marginBottom: '16px' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                {language === 'en' ? 'Clock In Required' : 'Debe Fichar'}
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '24px', maxWidth: '400px' }}>
                {language === 'en'
                  ? 'You must be clocked in to report equipment issues'
                  : 'Debe estar fichado para reportar problemas de equipo'}
              </p>
              <button
                onClick={navigateToTimeClock}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {language === 'en' ? 'Go to Time Clock' : 'Ir al Reloj'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.iconCircle}>
            <Wrench size={24} color="white" />
          </div>
          <div>
            <h1 style={styles.headerTitle}>{t.title}</h1>
            <p style={styles.headerSubtitle}>{language === 'es' ? 'Reportar un problema' : 'Report an issue'}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {error && (
          <div style={styles.errorMessage}>{error}</div>
        )}

        {/* Equipment Selector - Searchable */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            {t.selectEquipment}
            <span style={styles.required}>*</span>
          </label>
          <div style={styles.searchContainer} data-search-container>
            <div style={styles.searchIcon}>
              <Search size={18} />
            </div>
            <input
              type="text"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => {
                if (!selectedEquipment) {
                  setShowDropdown(true);
                }
              }}
              placeholder="Search by name, number, or type..."
              style={styles.searchInput}
            />
            {showDropdown && filteredEquipment.length > 0 && (
              <div style={styles.dropdown}>
                {filteredEquipment.map((eq) => (
                  <div
                    key={eq.equipment_id}
                    onClick={() => handleSelectEquipment(eq)}
                    style={styles.dropdownItem}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <div style={styles.dropdownItemName}>
                      {eq.equipment_name} (#{eq.bradanna_equipment_number})
                    </div>
                    <div style={styles.dropdownItemDetails}>
                      {eq.equipment_type}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showDropdown && searchText && filteredEquipment.length === 0 && (
              <div style={styles.dropdown}>
                <div style={{ ...styles.dropdownItem, textAlign: 'center', color: '#9ca3af' }}>
                  No equipment found
                </div>
              </div>
            )}
          </div>

          {selectedEquipment && (
            <div style={styles.selectedEquipmentCard}>
              <div style={styles.selectedEquipmentName}>
                {selectedEquipment.equipment_name}
              </div>
              <div style={styles.selectedEquipmentDetails}>
                #{selectedEquipment.bradanna_equipment_number} • {selectedEquipment.equipment_type}
              </div>
            </div>
          )}
        </div>

        {/* Severity Level */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            {t.severity}
            <span style={styles.required}>*</span>
          </label>
          <div style={styles.severityGrid}>
            <button
              type="button"
              onClick={() => setSeverity('low')}
              style={styles.severityButton(severity === 'low', '#3b82f6')}
            >
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>🟢 {language === 'en' ? 'Low' : 'Bajo'}</div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>{t.low}</div>
            </button>
            <button
              type="button"
              onClick={() => setSeverity('medium')}
              style={styles.severityButton(severity === 'medium', '#f59e0b')}
            >
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>🟡 {language === 'en' ? 'Medium' : 'Medio'}</div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>{t.medium}</div>
            </button>
            <button
              type="button"
              onClick={() => setSeverity('high')}
              style={styles.severityButton(severity === 'high', '#ef4444')}
            >
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>🔴 {language === 'en' ? 'High' : 'Alto'}</div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>{t.high}</div>
            </button>
          </div>
        </div>

        {/* Description */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            {t.description}
            <span style={styles.required}>*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t.descriptionPlaceholder}
            style={styles.textarea}
          />
        </div>

        {/* Job Location (Auto-detected or Manual) */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            {t.selectJob}
            {autoSelectedJob && (
              <span style={{ color: '#22c55e', fontSize: '14px', marginLeft: '8px' }}>
                📍 {autoJobSource === 'machine' ? 'Machine GPS' : 'Auto-detected'}
              </span>
            )}
          </label>
          <CustomSelect
            value={selectedJob || ''}
            onChange={(value) => {
              setSelectedJob(Number(value) || null);
              setAutoSelectedJob(false);
              setAutoJobSource(null);
            }}
            placeholder={`${t.currentJob} / Unknown`}
            options={jobs.map((job) => ({
              value: job.job_id,
              label: `${job.job_name}${job.bradanna_job_number ? ` (#${job.bradanna_job_number})` : ''}`
            }))}
          />
        </div>

        {/* Photos - Multiple */}
        <div style={styles.photoSection}>
          <label style={styles.label}>{t.photo}</label>

          {photoPreviewUrls.length > 0 && (
            <div style={styles.photoGrid}>
              {photoPreviewUrls.map((url, index) => (
                <div key={index} style={styles.photoPreviewContainer}>
                  <img src={url} alt={`Issue photo ${index + 1}`} style={styles.photoPreview} />
                  <button onClick={() => removePhoto(index)} style={styles.removePhotoButton}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleTakePhoto}
            disabled={cameraCooldown}
            style={styles.cameraButton}
          >
            <Camera size={20} />
            {cameraCooldown ? 'Please wait...' : t.takePhoto}
          </button>

          {photos.length > 0 && (
            <p style={{ fontSize: '14px', color: '#64748b', marginTop: '12px' }}>
              {photos.length} photo(s) added
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          style={canSubmit && !isSubmitting ? styles.submitButton : styles.submitButtonDisabled}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              {t.submitting}
            </>
          ) : (
            <>
              <AlertTriangle size={20} />
              {t.submit}
            </>
          )}
        </button>
      </div>

      {/* Success Overlay */}
      {showSuccess && (
        <div style={styles.successOverlay}>
          <div style={styles.successCard}>
            <Check size={64} style={{ color: '#22c55e', margin: '0 auto 16px auto' }} />
            <h2 style={{ margin: '0 0 8px 0', color: '#22c55e' }}>{t.success}</h2>
            <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
              {t.successMessage}
            </p>
            <button
              onClick={resetForm}
              style={styles.submitButton}
            >
              {t.reportAnother}
            </button>
          </div>
        </div>
      )}

      {/* Camera Component (Android only) */}
      {showCamera && (
        <CameraComponent
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
        />
      )}

    </div>
  );
};
